import { useEffect, useState } from "react";
import type { Lang } from "../lib/i18n";
import { pick } from "../lib/i18n";
import { ErrorBoundary } from "../components/ErrorBoundary";
import { logAudit } from "./lib/audit";
import { OverviewTab } from "./tabs/OverviewTab";
import { SystemsTab } from "./tabs/SystemsTab";
import { PathsTab } from "./tabs/PathsTab";
import { PoliciesTab } from "./tabs/PoliciesTab";
import { QATab } from "./tabs/QATab";
import { AnalyticsTab } from "./tabs/AnalyticsTab";
import { AuditTab } from "./tabs/AuditTab";
import { TeamTab } from "./tabs/TeamTab";
import { SmtpTab } from "./tabs/SmtpTab";
import { UsersTab } from "./tabs/UsersTab";
import { WelcomeMessagesTab } from "./tabs/WelcomeMessagesTab";
import { ChecklistTab } from "./tabs/ChecklistTab";

type Tab = "home" | "systems" | "paths" | "policies" | "qa" | "checklist" | "analytics" | "audit" | "team" | "smtp" | "welcome" | "users";

const TABS: Array<{ id: Tab; ar: string; en: string; icon: string }> = [
  { id: "home", ar: "النظرة العامة", en: "Overview", icon: "🏠" },
  { id: "systems", ar: "الأنظمة", en: "Systems", icon: "🗂" },
  { id: "paths", ar: "المسارات الوظيفية", en: "Career Paths", icon: "🗺️" },
  { id: "policies", ar: "السياسات والإجراءات", en: "Policies & Procedures", icon: "📖" },
  { id: "qa", ar: "أسئلة الأقسام", en: "Department Q&A", icon: "💬" },
  { id: "checklist", ar: "قائمة المراجعة الأساسية", en: "Essential Checklist", icon: "✅" },
  { id: "analytics", ar: "التحليلات والتقارير", en: "Analytics & Reports", icon: "📊" },
  { id: "audit", ar: "سجل التدقيق", en: "Audit Log", icon: "📜" },
  { id: "team", ar: "فريق تقنية المعلومات", en: "IT Team", icon: "👥" },
  { id: "smtp", ar: "إعدادات البريد", en: "Email / SMTP", icon: "✉️" },
  { id: "welcome", ar: "رسائل الترحيب", en: "Welcome Messages", icon: "👋" },
  { id: "users", ar: "المستخدمون", en: "Admin Users", icon: "⚙" },
];

/** Internal admin dashboard shell (Ct) — 12-tab management console. */
export function AdminDashboard({ lang, onExit, onLogout }: { lang: Lang; onExit: () => void; onLogout: () => void }) {
  const t = (ar: string, en: string) => pick(lang, ar, en);
  const [tab, setTab] = useState<Tab>("home");

  useEffect(() => { logAudit("admin_login", "Admin dashboard opened"); }, []);

  return (
    <div className="kd-admin-page" dir={lang === "ar" ? "rtl" : "ltr"}>
      <div className="kd-admin-header">
        <div>
          <h1>{t("لوحة التحكم الإدارية", "Admin Dashboard")}</h1>
          <p>{t("إدارة الأنظمة، فريق الدعم، إعدادات البريد، وعرض تقارير الموظفين الذين أكملوا الرحلة.", "Manage systems, support team, email settings, and view reports of employees who completed the journey.")}</p>
        </div>
        <div className="kd-admin-header-actions">
          <button className="kd-btn-secondary" onClick={onExit}>← {t("العودة للرحلة", "Back to Journey")}</button>
          <button className="kd-btn-secondary kd-btn-danger" onClick={onLogout}>{t("خروج", "Sign out")}</button>
        </div>
      </div>

      <div className="kd-admin-body">
        <nav className="kd-admin-tabs" role="tablist">
          {TABS.map((tb) => (
            <button key={tb.id} role="tab" aria-selected={tab === tb.id} className={`kd-admin-tab${tab === tb.id ? " active" : ""}`} onClick={() => setTab(tb.id)}>
              <span className="kd-admin-tab-icon">{tb.icon}</span>
              <span>{lang === "ar" ? tb.ar : tb.en}</span>
            </button>
          ))}
        </nav>

        <div className="kd-admin-tabpanel">
          <ErrorBoundary key={tab}>
            {tab === "home" && <OverviewTab lang={lang} />}
            {tab === "systems" && <SystemsTab lang={lang} />}
            {tab === "paths" && <PathsTab lang={lang} />}
            {tab === "policies" && <PoliciesTab lang={lang} />}
            {tab === "qa" && <QATab lang={lang} />}
            {tab === "checklist" && <ChecklistTab lang={lang} />}
            {tab === "analytics" && <AnalyticsTab lang={lang} />}
            {tab === "audit" && <AuditTab lang={lang} />}
            {tab === "team" && <TeamTab lang={lang} />}
            {tab === "smtp" && <SmtpTab lang={lang} />}
            {tab === "welcome" && <WelcomeMessagesTab lang={lang} />}
            {tab === "users" && <UsersTab lang={lang} />}
          </ErrorBoundary>
        </div>
      </div>
    </div>
  );
}
